import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';
import * as yup from 'yup';
import _ from '@lodash';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import { useTranslation } from 'react-i18next';

import UserService from 'app/admin/user/service/UserService';

const useStyles = makeStyles(theme => ({
	root: {
		background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${darken(theme.palette.primary.dark, 0.5)} 100%)`,
		color: theme.palette.primary.contrastText
	},
	leftSection: {}
}));

const defaultValues = {
	email: '',
	newPassword: '',
	newPasswordAgain: ''
};

const _backendMsg = (item) => {
	if (!item) return '';
	if (typeof item === 'string') return item;

	const direct = item.directMessage === true;
	const msg = direct ? item.message : (item.message || item.key);

	return msg ? String(msg) : '';
};

const _applyPropertiesErrors = (properties, setError, { fieldMap = {}, fallbackField = 'email' } = {}) => {
	if (!properties || typeof properties !== 'object') return false;

	let did = false;

	Object.entries(properties).forEach(([field, raw]) => {
		const list = Array.isArray(raw) ? raw : [raw];
		const msg = list.map(_backendMsg).filter(Boolean).join(' • ');
		if (!msg) return;

		const mapped = fieldMap[field] || fallbackField;
		setError(mapped, { type: 'server', message: msg });
		did = true;
	});

	return did;
};

function PasswordRequest() {
	const classes = useStyles();
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const { t } = useTranslation(['auth', 'validation']);

	const token = useMemo(() => {
		const params = new URLSearchParams(location.search || '');
		return params.get('token') || '';
	}, [location.search]);

	const tokenMode = !!token;

	const [loading, setLoading] = useState(false);
	const [completed, setCompleted] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const schema = useMemo(() => {
		if (tokenMode) {
			return yup.object().shape({
				newPassword: yup
					.string()
					.required(t('validation:PASSWORD_REQUIRED'))
					.min(5, t('validation:PASSWORD_IS_TOO_SHORT')),
				newPasswordAgain: yup
					.string()
					.oneOf([yup.ref('newPassword'), null], t('validation:PASSWORD_DONT_MATCH'))
					.required(t('validation:PASSWORD_REQUIRED'))
			});
		}

		return yup.object().shape({
			email: yup
				.string()
				.email(t('validation:EMAIL_IS_NOT_VALID'))
				.required(t('validation:EMAIL_IS_REQUIRED'))
		});
	}, [t, tokenMode]);

	const resolver = useMemo(() => yupResolver(schema), [schema]);

	const { control, setValue, setError, formState, handleSubmit, reset, trigger } = useForm({
		mode: 'onChange',
		defaultValues,
		resolver
	});

	const { isValid, dirtyFields, errors } = formState;

	useEffect(() => {
		// token mód váltásnál reseteljük a formot és a completed állapotot
		reset(defaultValues);
		setCompleted(false);

		// az aktuális mód mezőit "dirty+validate" állapotba tesszük
		if (tokenMode) {
			setValue('newPassword', '', { shouldDirty: true, shouldValidate: true });
			setValue('newPasswordAgain', '', { shouldDirty: true, shouldValidate: true });
		} else {
			setValue('email', '', { shouldDirty: true, shouldValidate: true });
		}

		trigger();
	}, [tokenMode, reset, setValue, trigger]);

	function onSubmit(model) {
		setLoading(true);

		if (tokenMode) {
			UserService.resetPassword(token, model.newPassword, model.newPasswordAgain)
				.then(resp => {
					setLoading(false);

					// ha a backend bool-t ad, vagy {successful:true}-t, mindkettőt kezeljük
					const ok = resp === true || resp?.successful === true;

					if (ok) {
						dispatch(showMessage({ message: t('NEW_PASSWORD_SET_MESSAGE') }));
						history.push('/login');
						return;
					}

					// {successful:false, properties:{...}} eset
					const applied = _applyPropertiesErrors(resp?.properties, setError, {
						fieldMap: {
							password: 'newPassword',
							newPassword: 'newPassword',
							passwordAgain: 'newPasswordAgain',
							newPasswordAgain: 'newPasswordAgain'
						},
						fallbackField: 'newPasswordAgain'
					});

					dispatch(showMessage({ message: t('NEW_PASSWORD_SET_FAILED_MESSAGE') }));

					// ha nem jött mező-specifikus hiba, akkor is ürítünk + validálunk
					setValue('newPassword', '', { shouldDirty: true, shouldValidate: true });
					setValue('newPasswordAgain', '', { shouldDirty: true, shouldValidate: true });

					// opcionális fallback, ha nem volt properties
					if (!applied) {
						setError('newPasswordAgain', { type: 'server', message: t('NEW_PASSWORD_SET_FAILED_MESSAGE') });
					}
				})
				.catch(() => {
					setLoading(false);
					dispatch(showMessage({ message: t('NEW_PASSWORD_SET_FAILED_MESSAGE') }));
					setError('newPasswordAgain', { type: 'server', message: t('NEW_PASSWORD_SET_FAILED_MESSAGE') });
				});

			return;
		}

		// EMAIL MODE: reset email kérése
		UserService.requestPasswordReset(model.email)
			.then(resp => {
				setLoading(false);

				// FONTOS: resp lehet objektum is, pl. {successful:false, properties:{...}}
				const ok = resp === true || resp?.successful === true;

				if (ok) {
					setCompleted(true);
					return;
				}

				// a te példád: properties.passwordAgain -> email alá
				const applied = _applyPropertiesErrors(resp?.properties, setError, {
					fieldMap: { passwordAgain: 'email', email: 'email' },
					fallbackField: 'email'
				});

				dispatch(showMessage({ message: t('NEW_PASSWORD_REQUEST_FAILED') }));

				// ha nem tudtunk properties-ből mezőhibát képezni, adjunk egy fallback üzenetet
				if (!applied) {
					const msg = _backendMsg(resp) || t('NEW_PASSWORD_REQUEST_FAILED');
					setError('email', { type: 'server', message: msg });
				}

				setValue('email', '', { shouldDirty: true, shouldValidate: true });
			})
			.catch(() => {
				setLoading(false);
				dispatch(showMessage({ message: t('NEW_PASSWORD_REQUEST_FAILED') }));
				setError('email', { type: 'server', message: t('NEW_PASSWORD_REQUEST_FAILED') });
				setValue('email', '', { shouldDirty: true, shouldValidate: true });
			});
	}

	return (
		<div className={clsx(classes.root, 'flex flex-col flex-auto items-center justify-center flex-shrink-0 p-16 md:p-24')}>
			<motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} className="flex min-w-320 max-w-400 md:max-w-3xl rounded-20 shadow-2xl overflow-hidden">
				<Card className={clsx(classes.leftSection, 'flex flex-col w-full max-w-sm items-center justify-center shadow-0 bg-transparent py-0')} square>
					<CardContent className="flex flex-col gap-0 items-center justify-center w-full px-0 py-0 max-w-320 bg-white bg-opacity-95">

						<motion.div className="w-full py-20" initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.2 } }}>
							<div className="w-full flex gap-16 items-stretch justify-center">
								<img className="w-96 h-auto z-10" src="assets/images/logos/mol_logo.svg" alt="MOL" />
								<div className="flex flex-col justify-center text-gray-800 gap-5 rounded-r-xl"><div className="text-28 leading-none font-bold opacity-80">Pathfinder</div></div>
							</div>
						</motion.div>

						<div className="w-full bg-white rounded-2xl px-20 py-24 shadow-2xl">
							{tokenMode ? (
								// TOKEN MODE UI
								<div className="w-full">
									<div className="pb-24 text-12 opacity-70">{t('PLEASE_SET_PASSWORD_MESSAGE')}</div>

									<form className="flex flex-col justify-center w-full" onSubmit={handleSubmit(onSubmit)}>
										<Controller
											name="newPassword"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													className="mb-16"
													label={t('PASSWORD')}
													error={!!errors.newPassword}
													helperText={errors?.newPassword?.message}
													variant="outlined"
													autoComplete="new-password"
													InputProps={{
														className: 'pr-2',
														type: showPassword ? 'text' : 'password',
														endAdornment: (
															<InputAdornment position="end">
																<IconButton onClick={() => setShowPassword(!showPassword)}>
																	<Icon className="text-20" color="action">
																		{showPassword ? 'visibility' : 'visibility_off'}
																	</Icon>
																</IconButton>
															</InputAdornment>
														)
													}}
													required
												/>
											)}
										/>

										<Controller
											name="newPasswordAgain"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													className="mb-16"
													label={t('PASSWORD_AGAIN')}
													error={!!errors.newPasswordAgain}
													helperText={errors?.newPasswordAgain?.message}
													variant="outlined"
													autoComplete="new-password"
													InputProps={{
														className: 'pr-2',
														type: showPassword ? 'text' : 'password',
														endAdornment: (
															<InputAdornment position="end">
																<IconButton onClick={() => setShowPassword(!showPassword)}>
																	<Icon className="text-20" color="action">
																		{showPassword ? 'visibility' : 'visibility_off'}
																	</Icon>
																</IconButton>
															</InputAdornment>
														)
													}}
													required
												/>
											)}
										/>

										<Button
											type="submit"
											variant="contained"
											color="primary"
											className="w-full mx-auto mt-16"
											aria-label="SAVE PASSWORD"
											disabled={_.isEmpty(dirtyFields) || !isValid || loading}
											value="legacy"
										>
											{t('SAVE_PASSWORD')}
										</Button>
									</form>
								</div>
							) : (
								// EMAIL MODE UI
								<>
									{!completed ? (
										<div className="w-full">
											<div className="pb-24 text-12">
												<div className="pb-8 font-semibold">{t('PLEASE_SET_EMAIL_MESSAGE')}</div>
												<div className="">{t('PLEASE_SET_EMAIL_DESC')}</div>
											</div>

											<form className="flex flex-col justify-center w-full" onSubmit={handleSubmit(onSubmit)}>
												<Controller
													name="email"
													control={control}
													render={({ field }) => (
														<TextField
															{...field}
															className="mb-16"
															type="text"
															error={!!errors.email}
															helperText={errors?.email?.message}
															label={t('EMAIL')}
															InputProps={{
																className: 'pr-4',
																endAdornment: (
																	<InputAdornment position="end" className="ml-4">
																		<Icon className="text-20 mx-12" color="action">
																			email
																		</Icon>
																	</InputAdornment>
																)
															}}
															variant="outlined"
															required
														/>
													)}
												/>

												<Button
													type="submit"
													variant="contained"
													color="primary"
													className="w-full mx-auto mt-8"
													aria-label="REQUEST PASSWORD"
													disabled={_.isEmpty(dirtyFields) || !isValid || loading}
													value="legacy"
												>
													{t('NEW_PASSWORD_REQUEST')}
												</Button>
											</form>
										</div>
									) : (
										<div className="w-full">{t('NEW_PASSWORD_REQUEST_MESSAGE')}</div>
									)}
								</>
							)}
						</div>

						<div className={clsx('flex flex-col items-center justify-center', tokenMode ? 'pt-24' : 'pt-24')}>
							<div>
								<Link style={{ color: '#252F2E' }} className={clsx(tokenMode ? 'font-semibold' : 'font-semibold')} to="/login">
									{t('BACK_TO_LOGIN')}
								</Link>
							</div>
						</div>

					</CardContent>
				</Card>
			</motion.div>
		</div>
	);
}

export default PasswordRequest;
